import { brand } from '../brand.js';
const P = brand.palette;

// Omnitok BI showcase mocks — retail / listings / sellers / marketplaces
// / pricing / brand-presence context. Numbers are illustrative;
// shapes match the lib's component contracts so changing values here
// is purely cosmetic.

// ── KPI ──────────────────────────────────────────────────
export const sparkData = [42, 55, 48, 67, 71, 85, 94, 101, 98, 112, 120, 127];

// ── Bar / Column ──────────────────────────────────────────
// Active listings tracked per month (Jan–Aug)
export const barData = [
  { label: 'Jan', value: 4200 }, { label: 'Feb', value: 3800 }, { label: 'Mar', value: 5200 },
  { label: 'Apr', value: 6100 }, { label: 'May', value: 5900 }, { label: 'Jun', value: 6800 },
];
// Top retailers by tracked SKUs. The last item carries a real-world
// long SKU-concatenation label (2026-07-14 feedback) so the gallery
// demos the adaptive label gutter + 2-line wrap of the horizontal bars.
export const hBarData = [
  { label: 'Amazon',         value: 4120 },
  { label: 'MediaMarkt',     value: 1870 },
  { label: 'Currys',         value: 1450 },
  { label: 'El Corte Inglés', value: 980 },
  { label: 'Worten',          value: 760 },
  { label: 'M0H56AL + M0H54AL + M0H55AL + 1VV22AL X2', value: 640 },
];

// ── Line ──────────────────────────────────────────────────
// Daily price-change events captured
export const lineData = [
  { label: 'Jan', value: 12000 }, { label: 'Feb', value: 14000 }, { label: 'Mar', value: 11000 },
  { label: 'Apr', value: 16000 }, { label: 'May', value: 18000 }, { label: 'Jun', value: 17500 },
  { label: 'Jul', value: 21000 }, { label: 'Aug', value: 23000 },
];
// Multi-line: listings tracked by category over time. Monitors ends flat
// (Jun = May): its legend entry keeps the delta chip's slot but draws no
// grey "—" pill (2026-10-23).
export const multiLineSeries = [
  { name: 'Laptops',   data: [1200, 1350, 1480, 1620, 1550, 1780] },
  { name: 'Printers',  data: [900,  950,  1050, 1180, 1120, 1300] },
  { name: 'Monitors',  data: [600,  680,  740,  820,  790,  790]  },
];
export const multiLineLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

// ── Pie / Donut ───────────────────────────────────────────
// Listings share by product category
export const pieData = [
  { label: 'Laptops',     value: 34 },
  { label: 'Printers',    value: 28 },
  { label: 'Monitors',    value: 21 },
  { label: 'Accessories', value: 17 },
];

// ── Stacked Bar (flat format: { label, Key1: val, Key2: val }) ──
// Listings per quarter, stacked by category
export const stackedData = [
  { label: 'Q1', 'Laptops': 1200, 'Printers': 900,  'Monitors': 600 },
  { label: 'Q2', 'Laptops': 1400, 'Printers': 1000, 'Monitors': 750 },
  { label: 'Q3', 'Laptops': 1600, 'Printers': 1150, 'Monitors': 850 },
  { label: 'Q4', 'Laptops': 1800, 'Printers': 1300, 'Monitors': 950 },
];
export const stackedKeys = ['Laptops', 'Printers', 'Monitors'];

// ── Grouped Bar (same flat format) ───────────────────────
// YoY listings tracked
export const groupedData = [
  { label: 'Jan', '2024': 3400, '2025': 4200 },
  { label: 'Feb', '2024': 2900, '2025': 3800 },
  { label: 'Mar', '2024': 4100, '2025': 5200 },
  { label: 'Apr', '2024': 4800, '2025': 6100 },
];
export const groupedKeys = ['2024', '2025'];

// ── Scatter ───────────────────────────────────────────────
// Listings (x = avg price index, y = compliance %) — deterministic so
// reload doesn't reshuffle the cluster.
const _scatterRng = (() => {
  let s = 4242;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
})();
export const scatterData = Array.from({ length: 30 }, (_, i) => ({
  x: 10 + _scatterRng() * 80,
  y: 20 + _scatterRng() * 60,
  r: 3 + _scatterRng() * 6,
  label: `SKU-${1000 + i}`,
}));

// ── Bubble ────────────────────────────────────────────────
// Per-marketplace: x=avg price, y=compliance %, size=listings count
export const bubbleData = [
  { label: 'Amazon',     x: 75, y: 82, size: 18 },
  { label: 'MediaMarkt', x: 65, y: 70, size: 12 },
  { label: 'Currys',     x: 55, y: 65, size: 10 },
  { label: 'Worten',     x: 45, y: 58, size: 8  },
  { label: 'PCComp',     x: 35, y: 72, size: 7  },
];

// ── Radar ─────────────────────────────────────────────────
// Brand health scorecard
export const radarData = [
  { axis: 'Coverage',   value: 82 }, { axis: 'Compliance',  value: 76 },
  { axis: 'Velocity',   value: 88 }, { axis: 'Quality',     value: 71 },
  { axis: 'Inventory',  value: 79 }, { axis: 'Pricing',     value: 85 },
];

// ── Waterfall ─────────────────────────────────────────────
// Active listings: opening balance → adds/removes → closing
export const waterfallData = [
  { label: 'Opening', value: 8000, type: 'start' },
  { label: 'Added',   value: 2400, type: 'increase' },
  { label: 'Delisted', value: -1200, type: 'decrease' },
  { label: 'Reactivated', value: 900, type: 'increase' },
  { label: 'Stale',   value: -450, type: 'decrease' },
  { label: 'Closing', value: 9650, type: 'total' },
];

// ── Funnel ────────────────────────────────────────────────
// Marketplace activation funnel
export const funnelData = [
  { label: 'Discovered',   value: 12000 },
  { label: 'Catalogued',   value: 8600 },
  { label: 'Validated',    value: 6400 },
  { label: 'Tracked',      value: 4800 },
  { label: 'Compliant',    value: 3200 },
];

// ── Treemap ───────────────────────────────────────────────
// Listings share by HP product line
// 6 slices max so every band is tall enough to carry its label +
// bold value + share (redesign rule 1 — a mute band informs nothing).
export const treemapData = [
  { label: 'Laptops',      value: 3400, color: P[0] },
  { label: 'Printers',     value: 2800, color: P[1] },
  { label: 'Monitors',     value: 2100, color: P[2] },
  { label: 'Accessories',  value: 1600, color: P[3] },
  { label: 'Workstations', value: 1200, color: P[4] },
  { label: 'Scanners',     value: 950,  color: P[5] },
];

// ── Heatmap chart ────────────────────────────────────────
// Listings activity by weekday × month
const _hmRng = (() => {
  let s = 99;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
})();
export const heatmapChartData = Array.from({ length: 7 }, () =>
  Array.from({ length: 12 }, () => Math.round(_hmRng() * 100)),
);
export const heatmapChartRows = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
export const heatmapChartCols = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Bullet ────────────────────────────────────────────────
// KPI vs target
export const bulletData = [
  { label: 'MAP Compliance', value: 78, target: 85, max: 100, range: [0, 100] },
  { label: 'Listing Coverage', value: 65, target: 75, max: 100, range: [0, 100] },
  { label: 'Price Freshness',  value: 91, target: 90, max: 100, range: [0, 100] },
];

// ── Lollipop ──────────────────────────────────────────────
// Top SKUs by # distinct ASINs — includes a long concatenated SKU so
// the gallery demos the adaptive label channel + 2-line wrap (same
// treatment as the horizontal bars, PR #63 / lollipop follow-up).
export const lollipopData = [
  { label: 'W1105A X2', value: 58 }, { label: 'CF258X', value: 25 },
  { label: 'M0H55AL', value: 17 }, { label: 'SU700A', value: 17 },
  { label: 'M0H56AL + M0H54AL + M0H55AL + 1VV22AL X2', value: 15 },
  { label: 'F6V31AB', value: 15 },
];

// ── Stacked Area ──────────────────────────────────────────
// Same series/labels as MultiLine — listings tracked by category over time
export const stackedAreaSeries = multiLineSeries;
export const stackedAreaLabels = multiLineLabels;

// ── Tables ────────────────────────────────────────────────
// Top retailers / marketplaces — used by the table demos
const RETAILERS = [
  'Amazon', 'MediaMarkt', 'Currys', 'El Corte Inglés', 'Worten',
  'PC Componentes', 'BestBuy', 'Falabella', 'Ripley', 'Mercado Libre',
  'Linio', 'Carrefour', 'FNAC', 'Saturn', 'Otto', 'Cdiscount',
];
// Deterministic pseudo-random so mock stays stable across reloads
const rng = (seed) => {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
};
const r = rng(1337);
const rnd = (min, max) => Math.floor(min + r() * (max - min));
const rndFloat = (min, max, d = 1) => +(min + r() * (max - min)).toFixed(d);

export const tableColumns = [
  { key: 'retailer',    label: 'Retailer' },
  { key: 'listings',    label: 'Listings' },
  { key: 'compliance',  label: 'MAP %' },
  { key: 'coverage',    label: 'Coverage %' },
  { key: 'change',      label: 'Δ' },
];
export const tableRows = RETAILERS.map((retailer) => ({
  retailer,
  listings:   rnd(120, 1600),
  compliance: rnd(72, 96),
  coverage:   rnd(60, 95),
  change:     rndFloat(-5, 8, 1),
}));

export const sparklineTableColumns = [
  { key: 'retailer', label: 'Retailer' },
  { key: 'listings', label: 'Listings' },
  { key: 'growth',   label: 'Growth %' },
  { key: 'trend',    label: 'Trend' },
];
export const sparklineTableRows = RETAILERS.map((retailer) => {
  const base = rnd(20, 80);
  const trend = Array.from({ length: 9 }, (_, i) => base + rnd(-8, 18) + i * rnd(1, 4));
  return {
    retailer,
    listings: rnd(50, 1450),
    growth:   rndFloat(-8, 22, 1),
    trend,
  };
});

const statuses = ['Active', 'Warning', 'Critical', 'Pending', 'Inactive'];
export const statusTableColumns = [
  { key: 'retailer', label: 'Retailer' },
  { key: 'units',    label: 'Marketplaces' },
  { key: 'status',   label: 'Status' },
  { key: 'progress', label: 'Onboarding' },
  { key: 'delta',    label: 'Δ WoW' },
];
export const statusTableRows = RETAILERS.map((retailer, i) => ({
  retailer,
  units:    rnd(2, 28),
  status:   statuses[i % statuses.length],
  progress: rnd(25, 98),
  delta:    rndFloat(-6, 9, 1),
}));

// ── Trend Matrix Table (fiscal-quarter comparison) ────────────
// Literal data from the approved redesign mockup
// (design_tmp/redesign_widgets/1_executive.html) as the SQL-formatted
// strings the product actually emits: bare value on the first quarter
// (no previous), "value ▲/▼ ±x.x%" on later ones, "value —" for a
// zero delta, "x.x% ▲ ±x.x pp" on the percent metric row. The metric
// column carries the emoji-dot prefixes that drive the bar colors.
export const trendMatrixColumns = [
  { key: 'metrica', label: 'Métrica' },
  { key: 'q1', label: 'Q1 FY25' },
  { key: 'q2', label: 'Q2' },
  { key: 'q3', label: 'Q3' },
  { key: 'q4', label: 'Q4' },
  { key: 'q5', label: 'Q1 FY26' },
  { key: 'q6', label: 'Q2' },
  { key: 'q7', label: 'Q3 *' },
];
export const trendMatrixRows = [
  { metrica: 'Publicaciones',
    q1: '181,596', q2: '164,149 ▼ -9.6%', q3: '146,595 ▼ -10.7%', q4: '139,362 ▼ -4.9%',
    q5: '118,361 ▼ -15.1%', q6: '120,736 ▲ +2.0%', q7: '99,088 ▼ -17.9%' },
  { metrica: 'Sellers',
    q1: '9,755', q2: '10,835 ▲ +11.1%', q3: '9,567 ▼ -11.7%', q4: '9,648 ▲ +0.8%',
    q5: '9,480 ▼ -1.7%', q6: '8,534 ▼ -10.0%', q7: '8,335 ▼ -2.3%' },
  { metrica: '🔴 No Autorizados',
    q1: '9,332', q2: '10,425 ▲ +11.7%', q3: '9,170 ▼ -12.0%', q4: '9,336 ▲ +1.8%',
    q5: '9,151 ▼ -2.0%', q6: '8,202 ▼ -10.4%', q7: '8,026 ▼ -2.1%' },
  { metrica: '🟢 Autorizados',
    q1: '422', q2: '459 ▲ +8.8%', q3: '406 ▼ -11.5%', q4: '333 ▼ -18.0%',
    q5: '343 ▲ +3.0%', q6: '336 ▼ -2.0%', q7: '292 ▼ -13.1%' },
  { metrica: '🟣 1P',
    q1: '37', q2: '36 ▼ -2.7%', q3: '28 ▼ -22.2%', q4: '28 —',
    q5: '29 ▲ +3.6%', q6: '30 ▲ +3.4%', q7: '22 ▼ -26.7%' },
  { metrica: '% No Autorizados',
    q1: '95.7%', q2: '96.2% ▲ +0.5 pp', q3: '95.9% ▼ -0.3 pp', q4: '96.8% ▲ +0.9 pp',
    q5: '96.5% ▼ -0.3 pp', q6: '96.1% ▼ -0.4 pp', q7: '96.3% ▲ +0.2 pp' },
];

// ── Analytics ─────────────────────────────────────────────
// Calendar heatmap — daily price-change events through 2025 (deterministic)
const _calRng = (() => {
  let s = 7;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
})();
export const calendarHeatmapData = (() => {
  const d = {};
  const start = new Date(2025, 0, 1);
  for (let i = 0; i < 365; i++) {
    const dt = new Date(start); dt.setDate(dt.getDate() + i);
    const key = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    if (_calRng() > 0.3) d[key] = Math.floor(_calRng() * 12);
  }
  return d;
})();

// Project timeline — marketplace-expansion phases
export const timelineData = [
  { label: 'Discovery',     start: 0, end: 2,  progress: 1 },
  { label: 'Onboarding',    start: 1, end: 4,  progress: 0.8 },
  { label: 'Activation',    start: 3, end: 8,  progress: 0.5 },
  { label: 'Tracking',      start: 6, end: 11, progress: 0.2 },
  { label: 'Optimization',  start: 10, end: 12, progress: 0 },
];
export const timelineMonths = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Quality-score breakdown
export const scoreBreakdown = [
  { label: 'Coverage',    value: 82 },
  { label: 'Compliance',  value: 76 },
  { label: 'Freshness',   value: 88 },
  { label: 'Catalog',     value: 71 },
];

// ── Maps ──────────────────────────────────────────────────
// LATAM presence — sales / listings volume per country
// `numValue` (in K$) drives the shared magenta HEAT_RAMP fill — the
// same `{ value, numValue }` shape GenericChart emits for `map`
// charts. No hardcoded per-country colors (legacy morado removed).
export const saMapData = {
  CL: { value: '$1.2M', numValue: 1200 },
  AR: { value: '$890K', numValue: 890 },
  PE: { value: '$340K', numValue: 340 },
  CO: { value: '$210K', numValue: 210 },
  BR: { value: '$180K', numValue: 180 },
  BO: { value: '$95K',  numValue: 95 },
  UY: { value: '$78K',  numValue: 78 },
  EC: { value: '$45K',  numValue: 45 },
};
// Chile regions — sales by administrative region
export const clMapData = {
  RM:   { value: '$420K', numValue: 420 }, V:    { value: '$180K', numValue: 180 },
  VIII: { value: '$145K', numValue: 145 }, IV:   { value: '$98K',  numValue: 98  },
  X:    { value: '$76K',  numValue: 76  }, IX:   { value: '$54K',  numValue: 54  },
  II:   { value: '$42K',  numValue: 42  }, III:  { value: '$38K',  numValue: 38  },
  VII:  { value: '$32K',  numValue: 32  }, VI:   { value: '$28K',  numValue: 28  },
  I:    { value: '$22K',  numValue: 22  }, XIV:  { value: '$18K',  numValue: 18  },
  XV:   { value: '$15K',  numValue: 15  }, XVI:  { value: '$12K',  numValue: 12  },
  XI:   { value: '$8K',   numValue: 8   }, XII:  { value: '$6K',   numValue: 6   },
};

// ── Combo Chart ───────────────────────────────────────────
// Listings tracked + MAP compliance % over time
export const comboData = [
  { label: 'Jan', listings: 12000, compliance: 82 },
  { label: 'Feb', listings: 14000, compliance: 85 },
  { label: 'Mar', listings: 11000, compliance: 79 },
  { label: 'Apr', listings: 16000, compliance: 88 },
  { label: 'May', listings: 18000, compliance: 86 },
  { label: 'Jun', listings: 17500, compliance: 91 },
];

// ── Line + Forecast ───────────────────────────────────────
// Listings tracked, historical + projection
export const lineTrendData = [
  { label: 'Jan', value: 12000 }, { label: 'Feb', value: 14000 },
  { label: 'Mar', value: 11000 }, { label: 'Apr', value: 16000 },
  { label: 'May', value: 18000 }, { label: 'Jun', value: 17500 },
];
export const lineTrendForecast = [
  { label: 'Jun', value: 17500 },
  { label: 'Jul', value: 19200 }, { label: 'Aug', value: 20800 },
  { label: 'Sep', value: 21500 },
];

// ── Filters demo ──────────────────────────────────────────
// Product categories
export const chipOptions = ['Laptops', 'Printers', 'Monitors', 'Accessories', 'Software'];
// Retailers / marketplaces
export const regionOptions = [
  'Amazon', 'MediaMarkt', 'Currys', 'El Corte Inglés', 'Worten',
  'PC Componentes', 'BestBuy', 'Falabella', 'Ripley', 'Mercado Libre',
  'Linio', 'Carrefour', 'FNAC', 'Saturn', 'Otto', 'Cdiscount',
];
// Sellers
export const profesionalOptions = [
  'TechMarket Ltd', 'PriceSmart S.A.', 'EcomBrands GmbH',
  'Digital Plaza Inc', 'Retail Connect AG', 'Marketplace Hub',
  'OmniSeller Co', 'GlobalShop SRL', 'NextGen Distrib',
];
// Date presets (English BI standard)
export const datePresetOptions = [
  { label: 'Today',   value: 'today' },
  { label: '7 days',  value: '7d' },
  { label: '30 days', value: '30d' },
  { label: 'MTD',     value: 'mtd' },
  { label: 'YTD',     value: 'ytd' },
];
// Embedded filter bar — typical BI report scope
export const embeddedFilters = [
  { key: 'retailer', label: 'Retailer', options: ['Amazon', 'MediaMarkt', 'Currys', 'Worten'] },
  { key: 'period',   label: 'Period',   options: ['2025 Q1', '2025 Q2', '2025 Q3', '2025 Q4'], default: '2025 Q4' },
  { key: 'category', label: 'Category', options: ['Laptops', 'Printers', 'Monitors'] },
  { key: 'channel',  label: 'Channel',  options: ['Online', 'Offline', 'Mixed'] },
];
// Multi-select — categories
export const multiSelectOptions = ['Laptops', 'Desktops', 'Workstations', 'Printers', 'Monitors', 'Scanners', 'Accessories', 'Software'];
// Saved filter presets
export const savedFilterPresets = [
  { key: 'all',      label: 'All listings',   icon: '⊙', count: 9650 },
  { key: 'active',   label: 'Tracked',         icon: '●', count: 7240 },
  { key: 'new',      label: 'New this week',   icon: '★', count: 184 },
  { key: 'urgent',   label: 'MAP violations',  icon: '!', count: 28 },
];
// ── Scorecard mocks (epic v3) ──
// Bands are written DESCENDING with a catch-all last, the shape the
// engine validates at parse time. These are the mockup's own numbers,
// kept here to demonstrate the three states in one screen — NOT a
// recommendation: against real gating data (~30/100 global) these
// bands paint everything red, which is why the real bands are a
// client decision, not a default.
export const scoreBands = [
  { min: 95, tone: 'good' },
  { min: 85, tone: 'warn' },
  { min: Number.NEGATIVE_INFINITY, tone: 'bad' },
];
// One row per marketplace inside a country card — including a null,
// because "not measured this week" is a real state that must not
// render as a zero-width bar next to a "0".
export const meterRows = [
  { label: 'Amazon',   value: 98 },
  { label: 'Costco',   value: 91 },
  { label: 'Best Buy', value: 72 },
  { label: 'Newegg',   value: null },
];
export const statusChips = [
  { label: '96/100', value: 96 },
  { label: '88/100', value: 88 },
  { label: '68/100', value: 68 },
];
// Authorization mix per marketplace row (MPA / Non-MPA / 1P / HHO).
// The last row carries a NULL bucket on purpose: it demonstrates the
// default `onMissing: 'block'` refusing to draw rather than silently
// reweighting the survivors.
export const shareBarRows = [
  {
    label: 'Amazon',
    segments: [
      { key: 'mpa',    label: 'MPA',     value: 940, color: brand.palette[0] },
      { key: 'nonmpa', label: 'Non-MPA', value: 48,  color: brand.palette[1] },
      { key: '1p',     label: '1P',      value: 10,  color: brand.palette[2] },
      { key: 'hho',    label: 'HHO',     value: 2,   color: brand.palette[3] },
    ],
  },
  {
    label: 'Costco',
    segments: [
      { key: 'mpa',    label: 'MPA',     value: 610, color: brand.palette[0] },
      { key: 'nonmpa', label: 'Non-MPA', value: 150, color: brand.palette[1] },
      { key: '1p',     label: '1P',      value: 12,  color: brand.palette[2] },
      { key: 'hho',    label: 'HHO',     value: 3,   color: brand.palette[3] },
    ],
  },
  {
    label: 'Best Buy',
    segments: [
      { key: 'mpa',    label: 'MPA',     value: 402, color: brand.palette[0] },
      { key: 'nonmpa', label: 'Non-MPA', value: 140, color: brand.palette[1] },
      { key: '1p',     label: '1P',      value: null, color: brand.palette[2] },
      { key: 'hho',    label: 'HHO',     value: 4,   color: brand.palette[3] },
    ],
  },
];
// Ranked risks — 40 rows on purpose: the gallery demo has to PROVE the
// list scrolls the whole set instead of truncating it.
export const rankedRisks = Array.from({ length: 40 }, (_, i) => ({
  id: `risk-${i + 1}`,
  title: [
    'Brazil - Mercado Livre', 'Mexico - Mercado Libre', 'US - Best Buy',
    'Argentina - Mercado Libre', 'Colombia - Exito', 'Peru - Falabella',
    'Chile - Ripley', 'US - Amazon', 'Canada - Costco', 'Brazil - Magazine Luiza',
  ][i % 10] + (i >= 10 ? ` (${Math.floor(i / 10) + 1})` : ''),
  subtitle: ['Supplies cleanup', 'Unauthorized concentration', 'Residual exposure'][i % 3],
  chip: { label: i % 3 === 0 ? 'High' : i % 3 === 1 ? 'Medium' : 'Low', tone: i % 3 === 0 ? 'bad' : i % 3 === 1 ? 'warn' : 'good' },
}));
export const readoutLines = [
  { id: 'r1', title: 'Global health improved to 88 but remains below the proposed threshold.' },
  { id: 'r2', title: 'North America is broadly healthy; Best Buy remains the watchout.' },
  { id: 'r3', title: 'LATAM needs targeted cleanup on Mercado Livre / Mercado Libre.' },
];
// The shape the AI-analysis endpoint actually returns: a headline plus
// the sentences that carry the argument. The readout demo renders these
// as prose, NOT as RankedList subtitles — see the Callout entry.
export const readoutInsights = [
  {
    title: 'LATAM is the gap',
    summary:
      'Regional health sits at 17.2 against 42.8 in North America, which points at structural authorization problems rather than a one-off coverage event.',
  },
  {
    title: 'Best Buy is the single largest exposure',
    summary:
      'US - Best Buy carries the highest absolute count of unauthorized listings even though its share looks healthier than the LATAM marketplaces.',
  },
  {
    title: 'Seller share is the ceiling',
    summary:
      'Authorized seller share stays at 4.1%, so the index cannot move much on listing cleanup alone.',
  },
];
export const metaItems = [
  { label: 'Reporting period', value: 'Q3 FY26' },
  { label: 'Comparison', value: 'vs Q2 FY26' },
  { label: 'Coverage', value: '8 countries - 17 marketplaces' },
  { label: 'Grain', value: 'Weekly' },
];
export const definitionLines = [
  { label: '% approved', value: '100% - Non-MPA share' },
  { label: 'Delta', value: 'percentage-point change vs prior period, same filters' },
];
export const categoryTabs = [
  { value: 'supplies', label: 'Supplies' },
  { value: 'printing', label: 'Print HW' },
  { value: 'computing', label: 'PS Computing' },
  { value: 'attach', label: 'Attach' },
];
export const countryTabs = [
  { value: 'us', label: 'United States', count: 21367 },
  { value: 'ca', label: 'Canada', count: 5683 },
  { value: 'mx', label: 'Mexico', count: 15125 },
  { value: 'br', label: 'Brazil', count: 6311 },
];
export const regionStrip = [
  { label: 'North America', value: 42.8, delta: '+2.1 pts vs semana anterior', deltaTone: 'good' },
  { label: 'LATAM', value: 17.2, delta: '-0.4 pts vs semana anterior', deltaTone: 'bad' },
];
